import React, { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FetchProperties, SearchProperties } from "../../apis/Property";
import {
  FetchAccommodationLicense,
  ForceAccommodationLicense,
  VerifyAccommodationLicense,
} from "../../apis/Tourism";
import Paginate from "../../components/shared/paginate";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/shared/dialog";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const MINISTRY_ERROR_MESSAGES = {
  101: "No license or permit data found",
  102: "Permit number or license number is required",
  103: "Permit number and license number cannot both be provided",
  104: "ID number is not expected for this request type",
  105: "License number is invalid",
  107: "Commercial registration number is invalid",
  109: "ID number is required",
  110: "ID number is invalid",
  111: "Permit number is invalid",
  112: "CR number or 700 number is required",
  500: "Ministry of Tourism internal server error",
  LICENSE_NOT_ACTIVE:
    "Permit/license cannot be verified because it is expired or inactive",
  DOCUMENT_REQUIRED: "Please upload the permit/license document as a PDF file",
};

// Disabled for now (PO) — keep manual-add UI/handlers below, re-enable when needed.
const MANUAL_ADD_ENABLED = false;

const emptyForm = () => ({
  verificationType: "permit",
  permitNumber: "",
  idNumber: "",
  licenseNumber: "",
  crNumber: "",
  sevenHundredNumber: "",
  useSevenHundredNumber: false,
  documentFile: null,
  startDate: "",
  endDate: "",
});

const getMinistryErrorMessage = (code) => {
  if (!code) return "Ministry verification failed";
  return (
    MINISTRY_ERROR_MESSAGES[code] ||
    `Ministry verification failed (error code: ${code})`
  );
};

const getLicenseLabel = (license) => {
  if (!license || typeof license !== "object") return "None";
  const number = license.permitNumber || license.licenseNumber || "—";
  const kind = license.type === "permit" ? "Permit" : "License";
  if (license.manuallyAdded) return `${kind} ${number} (manual)`;
  if (license.verified) return `${kind} ${number} (verified)`;
  return `${kind} ${number}`;
};

const hostName = (host) => {
  if (!host) return "—";
  return [host.firstName, host.lastName].filter(Boolean).join(" ") || "—";
};

const TourismLicenses = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_LISTINGS);

  const [properties, setProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [existingLicense, setExistingLicense] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [step, setStep] = useState("form"); // form | rejected | manual
  const [rejectMessage, setRejectMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const limit = "10";

  const columns = useMemo(
    () => ["Title", "Host", "Email", "License", "Action"],
    [],
  );

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const response = await FetchProperties(currentPage, limit);
      if (response.success) {
        setProperties(response?.data?.listings || []);
        setTotalPages(response?.data?.totalPages || "1");
      } else {
        toast.error("Failed to fetch properties. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to fetch properties. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const searchProperties = async () => {
    setLoading(true);
    try {
      const response = await SearchProperties(keyword, currentPage, limit);
      if (response.success) {
        setProperties(response?.data?.listings || []);
        setTotalPages(response?.data?.totalPages || "1");
      } else {
        toast.error("Failed to search properties. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to search properties. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keyword) {
      searchProperties();
    } else {
      fetchProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, keyword]);

  useEffect(() => {
    if (keyword) {
      setCurrentPage("1");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const resetDialog = () => {
    setSelectedProperty(null);
    setExistingLicense(null);
    setForm(emptyForm());
    setStep("form");
    setRejectMessage("");
    setSaving(false);
  };

  const openLicenseDialog = async (property) => {
    if (!canManage) return;
    setSelectedProperty(property);
    setForm(emptyForm());
    setStep("form");
    setRejectMessage("");
    setExistingLicense(
      property.license && typeof property.license === "object"
        ? property.license
        : null,
    );
    setDialogOpen(true);

    try {
      const response = await FetchAccommodationLicense(property._id);
      setExistingLicense(response?.license || null);
    } catch (error) {
      // keep listing-embedded license if fetch fails
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    resetDialog();
  };

  const updateForm = (patch) => {
    setRejectMessage("");
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const validateForm = (requireDates = false) => {
    if (form.verificationType === "permit") {
      if (!/^\d{1,8}$/.test(form.permitNumber)) {
        toast.error("Enter a valid permit number (1–8 digits)");
        return false;
      }
      if (!/^\d{1,20}$/.test(form.idNumber)) {
        toast.error("Enter a valid ID number (1–20 digits)");
        return false;
      }
    } else {
      if (!/^\d{1,8}$/.test(form.licenseNumber)) {
        toast.error("Enter a valid license number (1–8 digits)");
        return false;
      }
      if (form.useSevenHundredNumber) {
        if (!/^70\d{8}$/.test(form.sevenHundredNumber)) {
          toast.error("700 number must be 10 digits starting with 70");
          return false;
        }
      } else if (!/^\d{1,10}$/.test(form.crNumber)) {
        toast.error("Enter a valid CR number (1–10 digits)");
        return false;
      }
    }

    if (!form.documentFile) {
      toast.error("Please upload a PDF document");
      return false;
    }
    if (form.documentFile.type !== "application/pdf") {
      toast.error("Document must be a PDF file");
      return false;
    }
    if (form.documentFile.size > 10 * 1024 * 1024) {
      toast.error("PDF must be 10MB or less");
      return false;
    }

    if (requireDates) {
      if (!form.startDate || !form.endDate) {
        toast.error("Start date and end date are required");
        return false;
      }
      if (new Date(form.endDate) < new Date(form.startDate)) {
        toast.error("End date must be on or after start date");
        return false;
      }
    }

    return true;
  };

  const buildFormData = (includeDates = false) => {
    const formData = new FormData();
    formData.append("propertyId", selectedProperty._id);
    formData.append("verificationType", form.verificationType);
    formData.append("document", form.documentFile);

    if (form.verificationType === "permit") {
      formData.append("permitNumber", form.permitNumber);
      formData.append("idNumber", form.idNumber);
    } else {
      formData.append("licenseNumber", form.licenseNumber);
      if (form.useSevenHundredNumber) {
        formData.append("sevenHundredNumber", form.sevenHundredNumber);
      } else {
        formData.append("crNumber", form.crNumber);
      }
    }

    if (includeDates) {
      formData.append("startDate", new Date(form.startDate).toISOString());
      formData.append("endDate", new Date(form.endDate).toISOString());
    }

    return formData;
  };

  const handleVerify = async () => {
    if (!selectedProperty || !validateForm(false)) return;

    setSaving(true);
    setRejectMessage("");
    try {
      const response = await VerifyAccommodationLicense(buildFormData(false));
      toast.success(response?.message || "License verified and saved");
      closeDialog();
      if (keyword) {
        searchProperties();
      } else {
        fetchProperties();
      }
    } catch (error) {
      const data = error?.response?.data;
      const message =
        data?.message || getMinistryErrorMessage(data?.ministryErrorCode);
      toast.error(message);
      setRejectMessage(message);
      if (MANUAL_ADD_ENABLED) {
        setStep("rejected");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleForceAdd = async () => {
    if (!selectedProperty || !validateForm(true)) return;

    setSaving(true);
    try {
      const response = await ForceAccommodationLicense(buildFormData(true));
      toast.success(response?.message || "License added manually");
      closeDialog();
      if (keyword) {
        searchProperties();
      } else {
        fetchProperties();
      }
    } catch (error) {
      const data = error?.response?.data;
      toast.error(
        data?.message ||
          data?.errors?.[0]?.msg ||
          "Failed to add license manually",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex flex-col gap-6">
            <h1 className="text-2xl dark:text-[#c7d8eb]">Tourism licenses</h1>

            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <div className="relative border max-w-[400px] border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search by property or host name"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${column}`}
                      className={`${
                        index === 0 ? "w-[28%]" : "w-[18%]"
                      } border-y border-gray-500 dark:border-white p-3 font-semibold`}
                    >
                      {column}
                    </div>
                  ))}
                </div>

                {loading && (
                  <div className="p-6 border-b border-gray-500 dark:border-white">
                    Loading…
                  </div>
                )}

                {!loading && properties.length === 0 && (
                  <div className="p-6 border-b border-gray-500 dark:border-white">
                    No properties found
                  </div>
                )}

                {!loading &&
                  properties.map((property) => (
                    <div key={property._id}>
                      <div className="hidden lg:flex">
                        <div className="w-[28%] p-3 border-b border-gray-500 dark:border-white">
                          {property.title || "—"}
                        </div>
                        <div className="w-[18%] p-3 border-b border-gray-500 dark:border-white">
                          {hostName(property.host)}
                        </div>
                        <div className="w-[18%] p-3 border-b border-gray-500 dark:border-white break-all">
                          {property.host?.email || "—"}
                        </div>
                        <div className="w-[18%] p-3 border-b border-gray-500 dark:border-white">
                          {getLicenseLabel(property.license)}
                        </div>
                        <div className="w-[18%] p-3 border-b border-gray-500 dark:border-white">
                          {canManage ? (
                            <button
                              type="button"
                              className="rounded-md bg-secondaryColor px-3 py-2 text-white text-sm"
                              onClick={() => openLicenseDialog(property)}
                            >
                              {property.license
                                ? "Update license"
                                : "Add license"}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-500">—</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 border border-gray-500 dark:border-white rounded-md p-3 mb-3 lg:hidden">
                        <div>
                          <span className="font-semibold mr-2">Title:</span>
                          {property.title || "—"}
                        </div>
                        <div>
                          <span className="font-semibold mr-2">Host:</span>
                          {hostName(property.host)}
                        </div>
                        <div className="break-all">
                          <span className="font-semibold mr-2">Email:</span>
                          {property.host?.email || "—"}
                        </div>
                        <div>
                          <span className="font-semibold mr-2">License:</span>
                          {getLicenseLabel(property.license)}
                        </div>
                        {canManage && (
                          <button
                            type="button"
                            className="rounded-md bg-secondaryColor px-3 py-2 text-white text-sm w-fit"
                            onClick={() => openLicenseDialog(property)}
                          >
                            {property.license
                              ? "Update license"
                              : "Add license"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        </Container>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
          else setDialogOpen(true);
        }}
      >
        <DialogContent className="max-w-screen w-[calc(100vw-20px)] lg:w-[640px] max-h-[calc(100vh-80px)] overflow-auto bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
          <DialogHeader>
            <DialogTitle>
              {MANUAL_ADD_ENABLED && step === "manual"
                ? "Manual license add"
                : MANUAL_ADD_ENABLED && step === "rejected"
                  ? "License verification rejected"
                  : "Add tourism license"}
            </DialogTitle>
            <DialogDescription>
              {selectedProperty?.title
                ? `Property: ${selectedProperty.title}`
                : ""}
              {selectedProperty?.host
                ? ` · Host: ${hostName(selectedProperty.host)}`
                : ""}
            </DialogDescription>
          </DialogHeader>

          {existingLicense && step === "form" && (
            <div className="rounded-md border border-gray-400 dark:border-white p-3 text-sm">
              Current: {getLicenseLabel(existingLicense)}
              {existingLicense.startDate || existingLicense.endDate ? (
                <div className="mt-1 text-xs opacity-80">
                  {existingLicense.startDate
                    ? `From ${new Date(existingLicense.startDate).toLocaleDateString()}`
                    : ""}
                  {existingLicense.endDate
                    ? ` · Until ${new Date(existingLicense.endDate).toLocaleDateString()}`
                    : ""}
                </div>
              ) : null}
            </div>
          )}

          {MANUAL_ADD_ENABLED && step === "rejected" && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-red-700 dark:text-red-300">
                {rejectMessage || "Ministry verification rejected this license."}
              </p>
              <p className="text-sm">
                You can cancel and correct the details, or manually add the
                license without Ministry validation.
              </p>
              <DialogFooter className="gap-2">
                <button
                  type="button"
                  className="rounded-md border border-gray-500 px-4 py-2"
                  onClick={() => setStep("form")}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-md bg-secondaryColor px-4 py-2 text-white"
                  onClick={() => setStep("manual")}
                  disabled={saving}
                >
                  Manual add
                </button>
              </DialogFooter>
            </div>
          )}

          {(step === "form" || (MANUAL_ADD_ENABLED && step === "manual")) && (
            <div className="flex flex-col gap-4">
              {!MANUAL_ADD_ENABLED && rejectMessage && step === "form" && (
                <p className="rounded-md border border-red-400 bg-red-50 dark:bg-red-950/40 dark:border-red-700 p-3 text-sm text-red-700 dark:text-red-300">
                  {rejectMessage}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  className={`rounded-md px-3 py-2 text-sm ${
                    form.verificationType === "permit"
                      ? "bg-secondaryColor text-white"
                      : "border border-gray-500"
                  }`}
                  onClick={() => updateForm({ verificationType: "permit" })}
                  disabled={saving}
                >
                  Private permit
                </button>
                <button
                  type="button"
                  className={`rounded-md px-3 py-2 text-sm ${
                    form.verificationType === "license"
                      ? "bg-secondaryColor text-white"
                      : "border border-gray-500"
                  }`}
                  onClick={() => updateForm({ verificationType: "license" })}
                  disabled={saving}
                >
                  Commercial license
                </button>
              </div>

              {form.verificationType === "permit" ? (
                <>
                  <label className="flex flex-col gap-1 text-sm">
                    Permit number
                    <input
                      className="rounded-md border border-gray-500 px-3 py-2 dark:bg-darkBg2"
                      value={form.permitNumber}
                      onChange={(e) =>
                        updateForm({
                          permitNumber: e.target.value.replace(/\D/g, "").slice(0, 8),
                        })
                      }
                      disabled={saving}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    ID number
                    <input
                      className="rounded-md border border-gray-500 px-3 py-2 dark:bg-darkBg2"
                      value={form.idNumber}
                      onChange={(e) =>
                        updateForm({
                          idNumber: e.target.value.replace(/\D/g, "").slice(0, 20),
                        })
                      }
                      disabled={saving}
                    />
                  </label>
                </>
              ) : (
                <>
                  <label className="flex flex-col gap-1 text-sm">
                    License number
                    <input
                      className="rounded-md border border-gray-500 px-3 py-2 dark:bg-darkBg2"
                      value={form.licenseNumber}
                      onChange={(e) =>
                        updateForm({
                          licenseNumber: e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 8),
                        })
                      }
                      disabled={saving}
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.useSevenHundredNumber}
                      onChange={(e) =>
                        updateForm({
                          useSevenHundredNumber: e.target.checked,
                          crNumber: "",
                          sevenHundredNumber: "",
                        })
                      }
                      disabled={saving}
                    />
                    Use 700 number instead of CR number
                  </label>
                  {form.useSevenHundredNumber ? (
                    <label className="flex flex-col gap-1 text-sm">
                      700 number
                      <input
                        className="rounded-md border border-gray-500 px-3 py-2 dark:bg-darkBg2"
                        value={form.sevenHundredNumber}
                        onChange={(e) =>
                          updateForm({
                            sevenHundredNumber: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10),
                          })
                        }
                        disabled={saving}
                      />
                    </label>
                  ) : (
                    <label className="flex flex-col gap-1 text-sm">
                      CR number
                      <input
                        className="rounded-md border border-gray-500 px-3 py-2 dark:bg-darkBg2"
                        value={form.crNumber}
                        onChange={(e) =>
                          updateForm({
                            crNumber: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10),
                          })
                        }
                        disabled={saving}
                      />
                    </label>
                  )}
                </>
              )}

              <label className="flex flex-col gap-1 text-sm">
                Document (PDF)
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) =>
                    updateForm({ documentFile: e.target.files?.[0] || null })
                  }
                  disabled={saving}
                />
                {form.documentFile ? (
                  <span className="text-xs opacity-80">
                    {form.documentFile.name}
                  </span>
                ) : null}
              </label>

              {MANUAL_ADD_ENABLED && step === "manual" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1 text-sm">
                    Start date
                    <input
                      type="date"
                      className="rounded-md border border-gray-500 px-3 py-2 dark:bg-darkBg2"
                      value={form.startDate}
                      onChange={(e) => updateForm({ startDate: e.target.value })}
                      disabled={saving}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    End date
                    <input
                      type="date"
                      className="rounded-md border border-gray-500 px-3 py-2 dark:bg-darkBg2"
                      value={form.endDate}
                      onChange={(e) => updateForm({ endDate: e.target.value })}
                      disabled={saving}
                    />
                  </label>
                </div>
              )}

              <DialogFooter className="gap-2">
                <button
                  type="button"
                  className="rounded-md border border-gray-500 px-4 py-2"
                  onClick={
                    MANUAL_ADD_ENABLED && step === "manual"
                      ? () => setStep("rejected")
                      : closeDialog
                  }
                  disabled={saving}
                >
                  {MANUAL_ADD_ENABLED && step === "manual" ? "Back" : "Close"}
                </button>
                {step === "form" ? (
                  <button
                    type="button"
                    className="rounded-md bg-secondaryColor px-4 py-2 text-white disabled:opacity-60"
                    onClick={handleVerify}
                    disabled={saving}
                  >
                    {saving ? "Verifying…" : "Verify & save"}
                  </button>
                ) : MANUAL_ADD_ENABLED ? (
                  <button
                    type="button"
                    className="rounded-md bg-secondaryColor px-4 py-2 text-white disabled:opacity-60"
                    onClick={handleForceAdd}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Add without validation"}
                  </button>
                ) : null}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TourismLicenses;
